// @flow
import React from 'react';
import _ from 'lodash';
import AnnotationUtil from '../queries/annotations/AnnotationUtil';
import { setShapeHandlers } from '../svg/SvgUtil';
import { isInRect, newId } from '../util/DOMUtil';
import Authenticator from '../protocol/Authenticator';
import { getClientXY } from '../util/EventUtil';
import { CommentFilterOption, KeyCode } from '../util/Constants';
import FileInfoUtil from '../util/FileInfoUtil';
import { animateScroll } from '../util/DOMUtil';
import CommentUtil from '../util/CommentUtil';
import Svg from '../live-draw/svg/Svg';
import SpatialComment from './SpatialComment';
import { jsonToSvg, svgToJson, updateAttachmentLocation, updateAttachmentOffset } from '../util/ShapeUtil';
import Store from '../store/Store';
import StoreListener from '../store/StoreListener';
import { getErrorMessage } from '../util/ErrorUtil';
import { isJqXhr } from '../util/ObjectUtil';
import {
  Annotation,
  AnnotationAttachment,
  AnnotationLocation,
  AnnotationThread,
  RepoItem,
  SpatialLocation,
  TemporalLocation,
  ZmCollection
} from '../autogenerated/ZoomBridge';
import {
  ACTIVE_COMMENT_ID,
  ASSET_SCALE_INFO,
  COMMENT_FILTER_BY,
  COMMENT_TEXT,
  COMMENTS,
  MULTIPAGE_ASSET_CURRENT_PAGE,
  SHOW_COMMENTS
} from '../store/Constants';
import KeyboardShortcuts from '../keyboard-shortcuts/KeyboardShortcuts';
import KeydownConfig from '../keyboard-shortcuts/KeydownConfig';

const debug = require('../debug/debug')('wc:comments:WithComments');

const MARKER_OFFSET = 16;
const storeItems = [
  { name: COMMENTS, defaultValue: [] },
  { name: COMMENT_FILTER_BY, defaultValue: CommentFilterOption.UNRESOLVED },
  { name: ACTIVE_COMMENT_ID, defaultValue: null },
  { name: SHOW_COMMENTS, defaultValue: true },
  { name: MULTIPAGE_ASSET_CURRENT_PAGE, defaultValue: 1 },
  { name: COMMENT_TEXT, defaultValue: '' },
  { name: ASSET_SCALE_INFO }
];

type Props = {
  asset: RepoItem,
  collection: ZmCollection,
  src: string,
  useLocalComments: boolean,
  COMMENTS: AnnotationThread[],
  COMMENT_FILTER_BY: string,
  ACTIVE_COMMENT_ID: number,
  SHOW_COMMENTS: boolean,
  COMMENT_TEXT: string,
  ASSET_SCALE_INFO: Object,
  MULTIPAGE_ASSET_CURRENT_PAGE: number
};

type State = {
  strokeWidth: number,
  spatialComments: AnnotationThread[],
  shapes: Object[],
  shape: ?Object,
  freehand: boolean,
  activeShapeId: ?string,
  activeCommentId: ?number,
  comment: ?AnnotationThread,
  comments: AnnotationThread[]
};

const WithComments = (getRootNode, getHostNode) => (Component) =>
  @StoreListener(storeItems)
  class WithComments extends React.Component<Props, State> {
    static WrappedComponent = Component;

    constructor(props) {
      super(props);

      this.state = {
        strokeWidth: 5,
        spatialComments: [],
        shapes: [],
        shape: null,
        freehand: false,
        activeShapeId: null,
        activeCommentId: null,
        comment: null,
        comments: []
      };

      this.init();
    }

    async componentDidMount() {
      
      setShapeHandlers({
        line: this.onLineClick,
        rect: this.onRectClick,
        ellipse: this.onEllipseClick,
        text: this.onTextClick
      });

      if (this.props.useLocalComments) {
        const comments = await this.fetchComments(this.props);
        this.setState({ comments });
        this.setComments(this.props, comments);
      } else {
        this.setComments(this.props);
      }

      this.addListner();
    }

    async componentWillReceiveProps(nextProps) {
      if (nextProps.src !== this.props.src) {
        this.init();
      }

      if (
        nextProps[COMMENTS] !== this.getComments() ||
        nextProps[COMMENT_FILTER_BY] !== this.props[COMMENT_FILTER_BY] ||
        nextProps[MULTIPAGE_ASSET_CURRENT_PAGE] !== this.props[MULTIPAGE_ASSET_CURRENT_PAGE]
      ) {
        if (nextProps.useLocalComments) {
          const comments = await this.fetchComments(nextProps);
          this.setState({ comments });
          this.setComments(nextProps, comments);
        } else {
          this.setComments(nextProps);
        }
      }
    }

    componentWillUnmount() {
      setShapeHandlers(null);
      this.removeListner();
    }

    componentDidUpdate(prevProps) {
      if(this.props[ACTIVE_COMMENT_ID] && prevProps[ACTIVE_COMMENT_ID] !== this.props[ACTIVE_COMMENT_ID]) {
        this.jumpToCommentPosition();
      }
    }

    addListner = ()=> {
      document.addEventListener('keyup', this.onKeyUp);
      KeyboardShortcuts.add(new KeydownConfig(('WITH_COMMENT'),this.onEscape, [KeyCode.ESCAPE]))
    }
  
    removeListner = ()=> {
      document.removeEventListener('keyup', this.onKeyUp);
      KeyboardShortcuts.remove('WITH_COMMENT');
    }

    
  onEscape = ()=> {
    this.onCommentFormClose();
  }

    jumpToCommentPosition() {
      const node = getRootNode(this._component);
      const commentNode = node.querySelector(`[data-commentId="${this.props[ACTIVE_COMMENT_ID]}"]`);
      commentNode && animateScroll(node, commentNode,   {axis:'BOTH', dest:'CENTER'})
    }

    init = () => {
      this._shapeIdToThreadId = {};
      this._ignoreClickEvent = false;
    };

    clearActiveCommentAndShape = (force: boolean = false) => {
      debug('[clearActiveCommentAndShape()]');
      const { comment, shape, activeCommentId, activeShapeId } = this.state;
      let nextState = {};

      if (force || (comment && !this.props[COMMENT_TEXT])) {
        nextState.comment = null;
      }
      if (shape) {
        nextState.shape = null;
      }
      if (activeCommentId) {
        const tmpComment: AnnotationThread = this.getComments().find(
          (c: AnnotationThread) => c.getThreadId() === activeCommentId
        );
        if (tmpComment) {
          debug('[clearActiveCommentAndShape()] calling markCommentAsReadIfNeeded(), comment: ', tmpComment);
          CommentUtil.markCommentAsReadIfNeeded(tmpComment, true);
        }
        nextState.activeCommentId = null;
      }
      if (activeShapeId) {
        nextState.activeShapeId = null;
      }

      if (this.props[ACTIVE_COMMENT_ID]) {
        debug('[clearActiveCommentAndShape()] setting ACTIVE_COMMENT_ID to null');
        Store.put(ACTIVE_COMMENT_ID, null);
      }

      debug('[clearActiveCommentAndShape()] nextState: ', nextState);
      this.setState(nextState);
    };
    isSaveCalled = false;
    saveAnnotation = async (comment: AnnotationThread): Promise<AnnotationThread> => {
      if (this.isSaveCalled) return false;
      try {
        debug('[saveAnnotation()] begin');
        const { asset, collection } = this.props;
        this.isSaveCalled = true;
        const fuidRrnInfo = AnnotationUtil.toFuidRrnInfo(asset.getFuid(), asset.getBid(), asset.getRrn());
        const collectionId = collection.getCollectionId();
        let savedComment = undefined;
        const response = await AnnotationUtil.saveAnnotation(fuidRrnInfo, comment, collectionId);
        debug('[saveAnnotation()] response: ', response);
        savedComment = response.getThreads()[0];

        if (!savedComment) {
          debug('[saveAnnotation()] !savedComment. return.');
          return null;
        }

        const comments = this.getComments()
          .filter((c) => c.getThreadId() !== savedComment.getThreadId())
          .concat(savedComment);

        this.updateComments(comments);
        return savedComment;
      } catch (err) {
        debug('[saveAnnotation()] error: ', err);
        window.setError(getErrorMessage(err));
        throw isJqXhr(err) ? new Error(err) : err;
      } finally {
        this.isSaveCalled = false;
      }
    };

    saveReply = (comment: AnnotationThread) => {
      //AnnotationUtil.appendNewReply(comment, reply);
      return this.saveAnnotation(comment);
    };

    deleteReply = async (comment: AnnotationThread, reply: Annotation) => {
      try {
        await AnnotationUtil.deleteAnnotations([reply.getCommentId()]);
        _.remove(AnnotationUtil.getReplies(comment), (el) => {
          return el.getCommentId() === reply.getCommentId();
        });
        this.forceUpdate();
      } catch (err) {
        debug('[deleteReply()] error: ', err);
        window.setError(err.toString());
      }
    };

    resolveAnnotation = async (comment: AnnotationThread) => {
      try {
        const isResolved: boolean = !comment.isResolved();
        const rrn: number = await AnnotationUtil.resolve(comment.getThreadId(), isResolved);
        if (rrn > 0) {
          const updatedComment = _.cloneDeep(comment);
          updatedComment.setResolved(isResolved);
          const updatedComments = this.getComments()
            .filter((c) => c.getThreadId() !== comment.getThreadId())
            .concat(updatedComment);

          Store.put(COMMENTS, updatedComments);
        }
      } catch (errorId) {
        window.setError(errorId.toString());
      }
    };

    deleteAnnotation = async (comment: AnnotationThread) => {
      await AnnotationUtil.deleteAnnotations([AnnotationUtil.getRootAnnotation(comment).getCommentId()]);
      const comments = this.getComments().filter((c) => c.getThreadId() !== comment.getThreadId());
      this.updateComments(comments);
    };

    //#region Comments

    fetchComments = (props: Props) => {
      const { asset, collection } = props;
      const info = AnnotationUtil.repoItemToFuidRrnInfo(asset);
      return new Promise((resolve) => {
        AnnotationUtil.getAnnotations(info, collection.getCollectionId(), 0, (getAnnotations) => {
          const annotations = getAnnotations.getAnnotations() || [];
          resolve(annotations);
        });
      });
    };

    getComments = (): AnnotationThread[] => {
      if (this.props.useLocalComments) {
        return this.state.comments;
      }

      return this.props[COMMENTS];
    };

    setComments = (props, comments: ?(AnnotationThread[])) => {
      comments = comments || props[COMMENTS];

      const asset: RepoItem = props.asset;
      const currentPage: number = props[MULTIPAGE_ASSET_CURRENT_PAGE];
      const filter: string = props[COMMENT_FILTER_BY];

      this._shapeIdToThreadId = {};

      let baseFilteredComments;
      if (filter === CommentFilterOption.ALL) {
        baseFilteredComments = comments;
      } else if (filter === CommentFilterOption.UNRESOLVED) {
        baseFilteredComments = comments.filter((c) => !c.isResolved());
      } else {
        baseFilteredComments = comments.filter((c) => c.isResolved());
      }

      if (FileInfoUtil.isMultiPage(asset)) {
        baseFilteredComments = baseFilteredComments.filter((c) => AnnotationUtil.getPageNumber(c) === currentPage);
      }

      const spatialComments = baseFilteredComments.filter((c) => !!AnnotationUtil.getSpatialLocation(c));
      const shapeComments = baseFilteredComments.filter((c) => {
        const arr = AnnotationUtil.getAttachmentsFromThread(c);
        return arr && arr.length > 0 && !!arr[0].getData();
      });

      const shapes = shapeComments.map((c) => {
        const attachment = AnnotationUtil.getAttachmentsFromThread(c)[0];
        const shape = svgToJson(attachment._data, attachment._creator);

        this._shapeIdToThreadId[shape.id] = c._threadId;
        return shape;
      });

      this.setState({ spatialComments, shapes });
    };

    updateComments = (comments: AnnotationThread[]) => {
      if (this.props.useLocalComments) {
        this.setState({ comments });
      } else {
        Store.put(COMMENTS, comments);
      }
    };

    onImageClick = (e) => {
      debug('[onImageClick()]');
      const { comment, activeShapeId, activeCommentId } = this.state;
      const validTags = ['img', 'svg'];
      if (!_.includes(validTags, e.target.nodeName.toLowerCase())) {
        /**
         * This gets triggered in two cases:
         * 1) There was a click outside the bounds of the image
         * 2) After a shape drag event
         */
        debug('[onImageClick()] Invalid tag ', e.target);
        if (!this._ignoreClickEvent) {
          this.clearActiveCommentAndShape();
        }
        return;
      }

      if (this._ignoreClickEvent) {
        debug('[onImageClick()] this._ignoreClickEvent is set');
        this.clearActiveCommentAndShape();
        return;
      }

      const [clientX, clientY] = getClientXY(e);
      const rect = getHostNode(this._component).getBoundingClientRect();

      if (comment || activeShapeId || activeCommentId) {
        debug('[onImageClick()] Hiding comment form');
        this.clearActiveCommentAndShape();
        return;
      }

      if (!isInRect(rect, clientX, clientY)) {
        debug('[onImageClick()] Not calling showCommentForm()');
        return;
      }

      debug('[onImageClick()] Calling showCommentForm()');
      this.showCommentForm(clientX - rect.left, clientY - rect.top);
    };

    showCommentForm = (clientX, clientY, shape = null, comment = null) => {
      const { scale } = this.props[ASSET_SCALE_INFO];
      const currentPage: number = this.props[MULTIPAGE_ASSET_CURRENT_PAGE];
      const { asset, collection } = this.props;

      const fuidRrnInfo = AnnotationUtil.toFuidRrnInfo(asset.getFuid(), asset.getBid(), asset.getRrn());
      const collectionId = collection.getCollectionId();

      if (!comment) {
        comment = AnnotationUtil.createNewComment(fuidRrnInfo, collectionId);
        AnnotationUtil.setWidthAndHeightOffset(comment, Math.floor(clientX / scale), Math.floor(clientY / scale));

        if (FileInfoUtil.isMultiPage(asset)) {
          AnnotationUtil.setPageNumber(comment, currentPage);
        }

        comment._annotationLocation._commentIndex = CommentUtil.maxCommentIndex(this.getComments()) + 1;
        AnnotationUtil.setRootReply(comment, '');

        if (shape) {
          AnnotationUtil.attachSvgToAnnotationThread(comment, 'drawing', shape);
        }
      }

      this.setState({ comment });
    };

    onMarkerClick = (comment: AnnotationThread) => {
      if (this.state.comment) {
        this.setState({ comment: null });
      } else {
        const activeCommentId = comment.getThreadId() !== this.state.activeCommentId ? comment.getThreadId() : null;
        if (!activeCommentId) {
          debug('[onMarkerClick()] calling markCommentAsReadIfNeeded()');
          CommentUtil.markCommentAsReadIfNeeded(comment, true);
        }
        Store.put(ACTIVE_COMMENT_ID, activeCommentId);
        this.setState({ activeCommentId: activeCommentId });
      }
    };

    onCommentFormSubmit = async (comment) => {
      try {
        await this.saveAnnotation(comment);
        Store.put(COMMENT_TEXT, '');
        this.clearActiveCommentAndShape(true);
      } catch (err) {
        debug('[onCommentFormSubmit()] error: ', err);
        throw err;
      }
    };

    onCommentFormClose = () => {
      this._ignoreClickEvent = true;
      setTimeout(() => (this._ignoreClickEvent = false), 100);
      this.clearActiveCommentAndShape(true);
    };

    onMoveComment = async (comment: AnnotationThread, diffX: number, diffY: number) => {
      const { scale } = this.props[ASSET_SCALE_INFO];
      const x = AnnotationUtil.getWidthOffset(comment);
      const y = AnnotationUtil.getHeightOffset(comment);
      const newX = x + diffX / scale;
      const newY = y + diffY / scale;
      AnnotationUtil.setWidthAndHeightOffset(comment, newX, newY);
      await updateAttachmentLocation(comment, newX, newY);
      return await this.saveAnnotation(comment);
    };

    //#endregion

    onRef = (component) => {
      this._component = component ? component._component : null;
    };

    //#region Shapes

    getNewShapeData = () => {
      return {
        id: newId(),
        creator: Authenticator.AUTH_DATA.username,
        stroke: 'red',
        strokeWidth: this.state.strokeWidth,
        fill: 'transparent'
      };
    };

    createCommentWithShape = async (shape) => {
      const currentPage: number = this.props[MULTIPAGE_ASSET_CURRENT_PAGE];
      const { asset } = this.props;
      const center = this.getCenter();
      const shapeSvg = await jsonToSvg(shape);

      const attachment = new AnnotationAttachment()
        .setCreator(Authenticator.AUTH_DATA.username)
        .setData(shapeSvg)
        .setDataType('SVG')
        .setFileName('drawing')
        .setCommentId(-1);

      const annotation = new Annotation()
        .setCreator(Authenticator.AUTH_DATA.username)
        .setAttachments([attachment])
        .setText('');

      const location = new AnnotationLocation().setCommentIndex(CommentUtil.maxCommentIndex(this.getComments()) + 1);

      const sLocation = new SpatialLocation().setWidthOffset(center.x).setHeightOffset(center.y);

      location.setSpatialLocation(sLocation);

      if (FileInfoUtil.isMultiPage(asset)) {
        const tLocation = new TemporalLocation().setFrameNumber(currentPage);
        location.setTemporalLocation(tLocation);
      }

      const thread = new AnnotationThread()
        .setAnnotationKind(AnnotationUtil.COMMENT)
        .setAnnotation(annotation)
        .setAnnotationLocation(location);

      return thread;
    };

    onCreateShape = async (shape) => {
      const comment = await this.createCommentWithShape(shape);
      let nextState = {
        shape,
        comment,
        activeShapeId: shape.id
      };

      if (this.state.freehand) {
        nextState.freehand = false;
      }

      this.setState(nextState);
      // this.props.liveDraw.onCreateShape(shape);
    };

    onKeyUp = (e) => {
      if (e.keyCode === KeyCode.ESCAPE && this.state.activeShapeId) {
        this.setState({ activeShapeId: null });
      }
    };

    getCenter = () => {
      const { scale } = this.props[ASSET_SCALE_INFO];
      const node = getRootNode(this._component);
      const hostNode = getHostNode(this._component);
      return {
        x:
          node.clientWidth < hostNode.clientWidth
            ? (node.scrollLeft + node.clientWidth / 2) / scale
            : hostNode.clientWidth / 2 / scale,
        y:  node.clientHeight < hostNode.clientHeight
        ? (node.scrollTop + node.clientHeight / 2) / scale
        : hostNode.clientHeight / 2 / scale
      };
    };

    onRectClick = () => {
      const { scale } = this.props[ASSET_SCALE_INFO];
      const size = 100 / scale;
      const offset = 0.5 * size;
      const center = this.getCenter();
      const shape = {
        ...this.getNewShapeData(),
        type: 'rect',
        x: center.x - offset,
        y: center.y - offset,
        width: size,
        height: size
      };

      this.onCreateShape(shape);
    };

    onEllipseClick = () => {
      const { scale } = this.props[ASSET_SCALE_INFO];
      const size = 50 / scale;
      const center = this.getCenter();

      const shape = {
        ...this.getNewShapeData(),
        type: 'ellipse',
        cx: center.x,
        cy: center.y,
        rx: size,
        ry: size
      };

      this.onCreateShape(shape);
    };

    onLineClick = () => {
      const { scale } = this.props[ASSET_SCALE_INFO];
      const size = 50 / scale;
      const center = this.getCenter();

      const shape = {
        ...this.getNewShapeData(),
        type: 'line',
        x1: center.x - size,
        y1: center.y - size,
        x2: center.x + size,
        y2: center.y + size
      };

      this.onCreateShape(shape);
    };

    onTextClick = () => {
      const center = this.getCenter();

      const shape = {
        ...this.getNewShapeData(),
        type: 'text',
        text: 'Write something...',
        x: center.x - 100,
        y: center.y - 13,
        width: 200,
        height: 50,
        fontSize: 22
      };

      this.onCreateShape(shape);
    };

    onUpdateShape = async (shape) => {
      debug('[onUpdateShape()]');
      this._ignoreClickEvent = true;
      setTimeout(() => (this._ignoreClickEvent = false), 100);

      const comment = this.getCommentFromShape(shape);
      if (!comment) {
        return;
      }

      const updatedComment = _.cloneDeep(comment);
      const svg = await jsonToSvg(shape);
      AnnotationUtil.getRootAnnotation(updatedComment)
        .getAttachments()[0]
        .setData(svg);

      updateAttachmentOffset(updatedComment, shape);
      //this.setState({ comment: updatedComment });
      if (!updatedComment.getThreadId()) {
        this.setState({ shape, comment: updatedComment });
        return;
      }

      return await this.saveAnnotation(updatedComment);
    };

    onDeleteShape = (shape) => {
      // TODO: uncomment this when we figure out how to delete comment attachments.
      // const comment = this.getCommentFromShape(shape);
      // if (!comment) {
      //   return;
      // }
      // const attachment = AnnotationUtil.getRootAnnotation(comment).getAttachments()[0];
      // attachment.setData('');
      // this.props.saveAnnotation(comment);
    };

    setActiveShapeId = (activeShapeId) => {
      this.setState({ activeShapeId });
    };

    getCommentFromShape = (shape): AnnotationThread => {
      const threadId = this._shapeIdToThreadId[shape.id];
      if (threadId) {
        return this.getComments().find((c) => c._threadId === threadId);
      }

      if (this.state.shape && this.state.shape.id === shape.id) {
        return this.state.comment;
      }

      return null;
    };

    //#endregion

    renderComments = () => {
      if (!this.props[SHOW_COMMENTS]) {
        return null;
      }

      const { asset } = this.props;
      const { scale } = this.props[ASSET_SCALE_INFO];
      let { spatialComments, comment, activeShapeId, activeCommentId } = this.state;
      const activeCommentIdInPanel = this.props[ACTIVE_COMMENT_ID];
      if (comment) {
        spatialComments = spatialComments.concat(comment);
      }

      return spatialComments.map((c: AnnotationThread, i: number) => {
        const loc = AnnotationUtil.getSpatialLocation(c);
        const x = Math.round(loc._widthOffset * scale) - MARKER_OFFSET;
        const y = Math.round(loc._heightOffset * scale) - MARKER_OFFSET;
        return (
          <SpatialComment
            key={c.getThreadId() || `fallback_key_${i}`}
            asset={asset}
            comment={c}
            isActive={
              c === comment ||
              c._threadId === activeShapeId ||
              c._threadId === activeCommentId ||
              c._threadId === activeCommentIdInPanel
            }
            isHighlighted={activeCommentIdInPanel && c._threadId === activeCommentIdInPanel}
            style={{ top: `${y}px`, left: `${x}px`, height:'32px', width: '32px' }}
            height={y}
            onMarkerClick={this.onMarkerClick}
            onClose={this.onCommentFormClose}
            onSubmit={this.onCommentFormSubmit}
            saveAnnotation={this.saveAnnotation}
            resolveAnnotation={this.resolveAnnotation}
            deleteAnnotation={this.deleteAnnotation}
            saveReply={this.saveReply}
            deleteReply={this.deleteReply}
            onMoveComment={this.onMoveComment}
          />
        );
      });
    };

    render() {
      const { scale } = this.props[ASSET_SCALE_INFO];
      const activeCommentIdInPanel = this.props[ACTIVE_COMMENT_ID];
      const { comment, shape, shapes, freehand, activeShapeId, activeCommentId } = this.state;

      const svgProps = {
        data: shape ? shapes.concat(shape) : shapes,
        hostNode: getHostNode(this._component),
        freehand,
        scale,
        activeShapeId,
        setActiveShapeId: this.setActiveShapeId,
        getNewShapeData: this.getNewShapeData,
        onCreateShape: this.onCreateShape,
        onUpdateShape: this.onUpdateShape,
        onDeleteShape: this.onDeleteShape
      };

      return (
        <Component
          {...this.props}
          ref={this.onRef}
          preventScroll={!(activeCommentId || comment || activeCommentIdInPanel)}
          renderComments={this.renderComments}
          onClick={this.onImageClick}>
          {this.props[SHOW_COMMENTS] ? <Svg {...svgProps} /> : null}
        </Component>
      );
    }
  };

export default WithComments;
